import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchJson } from '../../../helpers/fetch.js';
import { JSONtoArr } from './useServerSideData.jsx';
import useDebounce from '../../../hooks/useDebounce.js';

export function buildReq(startRow, endRow, sortModel = [], filterModel = {}) {
  return {
    startRow,
    endRow,
    sortModel,
    filterModel,
    rowGroupCols: [],
    groupKeys: [],
    pivotMode: false,
    pivotCols: [],
    valueCols: []
  };
}

function buildFilterModel(filterModel, quickFilterText) {
  if (!filterModel && !quickFilterText) return null;

  let newFilterModel = {};
  if (quickFilterText) {
    newFilterModel.global_search = {
      filter: quickFilterText,
      filterType: 'text',
      type: 'contains',
    };
  }
  if (filterModel) {
    newFilterModel = { ...newFilterModel, ...filterModel };
  }
  return newFilterModel;
};

function keyify(baseKey, path, body, filterModel, quickFilterText, sortModel) {
  return [
    baseKey,
    path,
    JSON.stringify(body || {}),
    JSON.stringify(filterModel || {}),
    String(quickFilterText || ''),
    JSON.stringify(sortModel || {})
  ];
}

function fetchRows(path, body, filterModel, quickFilterText, sortModel, signal) {
  return fetchJson(path, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      ...(body || {}),
      ...(sortModel ? {sortModel} : {}),
      filterModel: buildFilterModel(filterModel, quickFilterText)
    })
  });
}

export function useClientSideRows({
  baseKey = 'clientRows',
  fetchPath,
  fetchBody,
  quickFilterText,
  filterModel,
  sortModel,
  enabled = true,
  staleTime = 30_000
}) {
  const debouncedQuickFilterText = useDebounce(quickFilterText, 1000);

  const queryKey = useMemo(
    () => keyify(baseKey, fetchPath, fetchBody, filterModel, debouncedQuickFilterText, sortModel),
    [baseKey, fetchPath, fetchBody, filterModel, debouncedQuickFilterText, sortModel]
  );

  const q = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      fetchRows(fetchPath, fetchBody, filterModel, debouncedQuickFilterText, sortModel, signal),
    enabled: enabled && !!fetchPath,
    staleTime,
    keepPreviousData: true
  });

  const rowData = useMemo(() => {
    var rows = q.data && q.data.rows ? q.data.rows : q.data;
    return Array.isArray(rows) ? JSONtoArr(rows) : [];
  }, [q.data]);

  return {
    rowData,
    loading: q.isLoading || q.isFetching,
    error: q.error,
    refetch: q.refetch
  };
}
